import {
	Fragment,
	isValidElement,
	useMemo,
	useSyncExternalStore,
	type ReactNode,
} from 'react';

import { uiLanguageDirection } from './languages';
import {
	formatMessage,
	getLanguage,
	subscribe,
	translate,
	type TranslateArgs,
} from './runtime';
import type { ArgsOf, MessageKey, TagsOf } from './types';

/** The app language; re-renders when it changes. */
export function useLanguage(): string {
	return useSyncExternalStore(subscribe, getLanguage, getLanguage);
}

/**
 * The app language's writing direction -- `<html dir>`. For the few things
 * CSS logical properties can't mirror, like which arrow key means "next".
 */
export function useUiDirection(): 'ltr' | 'rtl' {
	return uiLanguageDirection(useLanguage());
}

/**
 * `t(key, values?)` for the current app language. Keys are always full
 * (`t('settings.theme.title')`) so one search finds a key everywhere it is
 * used, defined, or overridden. Arguments are typed from the English message.
 */
export function useT(): <K extends MessageKey>(
	key: K,
	...values: TranslateArgs<K>
) => string {
	const language = useLanguage();
	// A new function per language (not `translate` itself, whose identity
	// never changes), so a `useMemo`/`useEffect` keyed on `t` re-runs when
	// the language does.
	return useMemo(
		() =>
			(<K extends MessageKey>(key: K, ...values: TranslateArgs<K>) =>
				translate(key, ...values)) as typeof translate,
		// eslint-disable-next-line react-hooks/exhaustive-deps -- `language` is the point
		[language],
	);
}

type TagRenderers<K extends MessageKey> = [TagsOf<K>] extends [never]
	? { components?: never }
	: { components: Record<TagsOf<K>, (chunks: ReactNode) => ReactNode> };

type TValues<K extends MessageKey> = {} extends ArgsOf<K>
	? { values?: { [N in keyof ArgsOf<K>]: ArgsOf<K>[N] | ReactNode } }
	: { values: { [N in keyof ArgsOf<K>]: ArgsOf<K>[N] | ReactNode } };

/**
 * A message with rich text:
 *
 *     <T k="privacy.notice" components={{ link: (chunks) => <a href="/privacy">{chunks}</a> }} />
 *
 * Tag names are typed from the English message, so a renamed tag is a `tsc`
 * error. Keeps word order in the translator's hands instead of splitting a
 * sentence around the link.
 */
export function T<K extends MessageKey>(
	props: { k: K } & TValues<K> & TagRenderers<K>,
): ReactNode {
	useLanguage();
	const { k, values, components } = props as {
		k: K;
		values?: Record<string, unknown>;
		components?: Record<string, (chunks: ReactNode) => ReactNode>;
	};
	const tags = Object.fromEntries(
		Object.entries(components ?? {}).map(([tag, render]) => [
			tag,
			(chunks: Array<ReactNode>) => render(withKeys(chunks)),
		]),
	);
	const result = formatMessage(k, { ...values, ...tags });
	return Array.isArray(result) ? withKeys(result as Array<ReactNode>) : result;
}

function withKeys(parts: Array<ReactNode>): ReactNode {
	return parts.map((part, index) =>
		isValidElement(part) ? (
			<Fragment key={index}>{part}</Fragment>
		) : (
			part
		),
	);
}
